import React, { useState, useEffect } from 'react';
import { View } from 'react-native';
import Collapse from 'react-native-collapsible';

import { Card, VFButton, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { ShimmerWrapper } from '@vfgroup-oneplatform/foundation/Components/Shimmer';
import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import VFLottieView, { LottieTypes } from '@vfgroup-oneplatform/foundation/Components/VFLottieView';
import { usePrevious } from '@vfgroup-oneplatform/foundation/Utils/Hooks';
import { isFunction as _isFunction, get as _get } from 'lodash';
import PropTypes from 'prop-types';

import { paymentBreakDownLoadingConfig } from '../../Common/LoadingConfigurations/PaymentBreakdownLoading/PaymentBreakdownLoading.config';
import BillingTestKeys from '../../Utils/BillingTestKeys.js';
import HeadLine from '../HeadLine';
import VfView from '../VfView';

import styles from './ExpansionPanel.Styles';
import ExpansionPanelItem from './ExpansionPanelItem';
import ExpansionPanelITotal from './ExpansionPanelTotal';

function ExpansionPanel(props) {
  const Loader = props.loader ? (
    props.loader
  ) : (
    <ShimmerWrapper
      isLoading={props.isLoading}
      loadingConfig={paymentBreakDownLoadingConfig}
      containerStyle={styles.loaderContainer}
    />
  );

  const [isCollapsed, setIsCollapsed] = useState(true);
  const { theme, showMoreTestID } = props;

  const previousIsShowMoreLoading = usePrevious(props.isShowMoreLoading);

  useEffect(() => {
    if (!props.isShowMoreLoading && previousIsShowMoreLoading) {
      if (_get(props.calls, 'length', 0) > 0 || _get(props.detailsItems, 'length', 0) > 0) {
        setIsCollapsed(false);
      }
    }
  }, [props.isShowMoreLoading]);

  const handleExpandClick = () => {
    if (isCollapsed) {
      if (
        _get(props.calls, 'length', 0) > 0 ||
        _get(props.detailsItems, 'length', 0) > 0 ||
        !_isFunction(props.onShowMorePress)
      ) {
        setIsCollapsed(false);
      } else if (_isFunction(props.onShowMorePress)) {
        props.onShowMorePress(props.subscription);
      }
    } else {
      setIsCollapsed(true);
    }
  };

  const renderCTA = (withCTA, ctaProps, subscription) => {
    const { onPress } = ctaProps || {};
    if (!withCTA) {
      return undefined;
    }

    return (
      <VfView style={styles.ctaContainer}>
        <VFButton
          type={'secondary'}
          textStyle={styles.ctaText}
          {...ctaProps}
          onPress={() => {
            _isFunction(onPress) && onPress(subscription);
          }}
        />
      </VfView>
    );
  };

  const renderTotal = () => {
    return (
      <View style={[styles.bottomBorder(theme), styles.notCollapsedItem, styles.row]}>
        <VFText
          style={styles.totalText(theme)}
          i18nKey="billing_payment_breakdown_total_title"
          testKey={BillingTestKeys.expansionPanelBreakdownTotalLabelText}
        />
        <VFText
          style={[styles.totalText(theme), props.total.totalValueStyle]}
          i18nKey={`${props.total.totalValue}`}
          testKey={BillingTestKeys.expansionPanelBreakdownTotalValueText}
        />
      </View>
    );
  };

  const renderSummaryItems = () => {
    if (props.summaryItems?.length) {
      return (
        <VfView style={isCollapsed ? styles.bottomBorder(theme) : styles.collapsedItem(theme)}>
          {props.summaryItems.map((item, i) => {
            return (
              <ExpansionPanelItem
                key={i}
                {...item}
                removeBorderBottom={props.summaryItems.length - 1 === i}
                toggleModal={props.toggleModal}
                isCollapsed={isCollapsed}
              />
            );
          })}
        </VfView>
      );
    }
  };

  const renderDetailsItems = () => {
    return (
      <VfView
        style={
          _get(props.detailsItems, 'length', 0) > 0
            ? isCollapsed
              ? styles.bottomBorder(theme)
              : styles.collapsedItem(theme)
            : undefined
        }
      >
        {props.detailsItems.map((collapsedItem, i) => (
          <ExpansionPanelItem
            key={i}
            {...collapsedItem}
            removeBorderBottom={props.detailsItems.length - 1 === i}
            isCollapsed={isCollapsed}
            toggleModal={props.toggleModal}
          />
        ))}
      </VfView>
    );
  };

  const renderCalls = () => {
    return (
      <VfView style={_get(props.calls, 'length', 0) > 0 ? styles.collapsedItem(theme) : undefined}>
        {props.calls.map((call, i) => (
          <ExpansionPanelItem
            key={'calls' + i}
            {...call}
            toggleModal={props.toggleModal}
            isCollapsed={isCollapsed}
          />
        ))}
      </VfView>
    );
  };

  return (
    <Card style={styles.cardStyle(theme)} testID={BillingTestKeys.expansionPanelCard}>
      {props.isLoading ? (
        Loader
      ) : (
        <VfView justify="center" alignItems="center">
          <VfView xs={11}>
            <HeadLine {...props.headlineProps} style={styles.headline} />
            {props.total && props.showTotalAsSummary && isCollapsed && renderTotal()}

            {!props.showTotalAsSummary &&
              !props.showTotalWithDetailsItems &&
              isCollapsed &&
              renderSummaryItems()}

            {!props.showTotalAsSummary &&
              props.showTotalWithDetailsItems &&
              props.total &&
              isCollapsed &&
              renderDetailsItems()}

            {!props.showTotalAsSummary &&
              props.showTotalWithDetailsItems &&
              props.total &&
              isCollapsed &&
              renderTotal()}

            <Collapse collapsed={isCollapsed}>
              {renderSummaryItems()}
              {renderCalls()}
              {renderDetailsItems()}
              <VfView style={styles.bottomBorder(theme)}>
                {props.total && (
                  <ExpansionPanelITotal
                    totalLabel="billing_payment_breakdown_total_pay_title"
                    noVatLabel="billing_payment_breakdown_total_before_vat_title"
                    vatLabel="billing_payment_breakdown_vat_title"
                    {...props.total}
                  />
                )}
                {renderCTA(props.withCTA, props.ctaProps, props.subscription)}
              </VfView>
            </Collapse>
          </VfView>

          {props.isShowMoreLoading && (
            <VFLottieView
              type={LottieTypes.smallSpinner}
              autoPlay={true}
              loop={true}
              lottieViewStyle={styles.lottieViewStyle}
            />
          )}
          {!props.isShowMoreLoading && (
            <VFButton
              title={isCollapsed ? props.buttonTitleOpen : props.buttonTitleClose}
              type="secondary"
              style={styles.collapseButton}
              textStyle={styles.collapseButtonText(theme)}
              onPress={handleExpandClick}
              testID={showMoreTestID}
            />
          )}
        </VfView>
      )}
    </Card>
  );
}

ExpansionPanel.defaultProps = {
  calls: [],
  summaryItems: [],
  detailsItems: [],
  theme: LightThemeColors,
};

ExpansionPanel.propTypes = {
  headlineProps: PropTypes.object,
  summaryItems: PropTypes.array,
  detailsItems: PropTypes.array,
  calls: PropTypes.array,
  buttonTitleOpen: PropTypes.string,
  buttonTitleClose: PropTypes.string,
  loader: PropTypes.node,
  isLoading: PropTypes.bool,
  total: PropTypes.object,
  showTotalAsSummary: PropTypes.bool,
  subscription: PropTypes.object,
  withCTA: PropTypes.bool,
  ctaProps: PropTypes.object,
  isShowMoreLoading: PropTypes.bool,
  onShowMorePress: PropTypes.func,
  theme: PropTypes.object,
  showMoreTestID: PropTypes.string,
  showTotalWithDetailsItems: PropTypes.bool,
  toggleModal: PropTypes.func,
};

export default withTheme(ExpansionPanel);
